import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { motion } from 'framer-motion';
import { RotateCcw, Download, SlidersHorizontal, X, Eye } from 'lucide-react';
import CustomDatePicker from '../../components/dashboard/CustomDatePicker';
import Pagination from '../../components/Pagination';
import { getDoctorsByDate, downloadDoctorsExcel } from '../../redux/reportsSlice';
import { getDoctorById } from '../../redux/doctorSlice';
import { getCountries } from '../../redux/masterSlice';
import { getAllPatientsList } from '../../redux/patientSlice';
import { toast } from 'react-toastify';
import CommonLoader from '../../components/CommonLoader';
import { useTranslation } from 'react-i18next';
import DoctorProfileModal from '../../components/modals/DoctorProfileModal';
import { FilterPanel, FilterSection, FilterCountryList, FilterOptionList, FilterSearchableList, FilterPopover } from '../../components/common/FilterPanel';
import MasterLabel from '../../components/common/MasterLabel';

// Debounce hook
const useDebounce = (value, delay) => {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const handler = setTimeout(() => setDebouncedValue(value), delay);
        return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
};

const ReportsDoctors = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { doctorsByDate, loading, downloadLoading, totalItems, page, totalPages } = useSelector((state) => state.reports);
    const { selectedDoctor, loading: doctorLoading } = useSelector((state) => state.doctor);
    const { allPatientsList, isLoading: patientsLoading } = useSelector((state) => state.patient);
    const { countries } = useSelector((state) => state.master);

    const [activeTab, setActiveTab] = useState('month');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [dateErrors, setDateErrors] = useState({ startDate: '', endDate: '' });
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(5);
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const [tempFilters, setTempFilters] = useState({ gender: [], countryId: [], selectedPatient: [] });
    const [filters, setFilters] = useState({ gender: [], countryId: [], selectedPatient: [] });
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [patientSearch, setPatientSearch] = useState('');
    const isInitialRender = useRef(true);
    const filterButtonRef = useRef(null);
    const filterRef = useRef(null);

    const debouncedPatientSearch = useDebounce(patientSearch, 400);

    // Helper function to get date range based on tab
    const getDateRange = (tab) => {
        const today = new Date();
        let from = '';
        let to = '';

        switch (tab) {
            case 'today':
                from = to = today.toISOString().split('T')[0];
                break;
            case 'week':
                const weekAgo = new Date(today);
                weekAgo.setDate(today.getDate() - 7);
                from = weekAgo.toISOString().split('T')[0];
                to = today.toISOString().split('T')[0];
                break;
            case 'month':
                const monthAgo = new Date(today);
                monthAgo.setMonth(today.getMonth() - 1);
                from = monthAgo.toISOString().split('T')[0];
                to = today.toISOString().split('T')[0];
                break;
            case 'custom':
                from = startDate;
                to = endDate;
                break;
            default:
                from = to = today.toISOString().split('T')[0];
        }

        return { from, to };
    };

    // Validate dates
    const validateDates = (start, end) => {
        const errors = { startDate: '', endDate: '' };
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (start) {
            const startDateObj = new Date(start);
            startDateObj.setHours(0, 0, 0, 0);

            // Check if start date is valid
            if (isNaN(startDateObj.getTime())) {
                errors.startDate = t('reportsDoctors.invalid_date_format');
            }

            // Check if start date is after end date
            if (end) {
                const endDateObj = new Date(end);
                endDateObj.setHours(0, 0, 0, 0);
                if (startDateObj > endDateObj) {
                    errors.startDate = t('reportsDoctors.start_after_end');
                }
            }
        }

        if (end) {
            const endDateObj = new Date(end);
            endDateObj.setHours(0, 0, 0, 0);

            // Check if end date is valid
            if (isNaN(endDateObj.getTime())) {
                errors.endDate = t('reportsDoctors.invalid_date_format');
            }

            // Check if end date is in the future
            if (endDateObj > today) {
                errors.endDate = t('reportsDoctors.end_in_future');
            }

            // Check if end date is before start date
            if (start) {
                const startDateObj = new Date(start);
                startDateObj.setHours(0, 0, 0, 0);
                if (endDateObj < startDateObj) {
                    errors.endDate = t('reportsDoctors.end_before_start');
                }
            }
        }

        // Check if start date is required when end date is set
        if (end && !start) {
            errors.startDate = t('reportsDoctors.start_required');
        }

        setDateErrors(errors);
        return Object.values(errors).every(error => !error);
    };

    // Fetch data function
    const fetchDoctorsData = (page = 1) => {
        const { from, to } = getDateRange(activeTab);

        if (!from || !to) {
            if (activeTab === 'custom') {
                toast.warning(t('reportsDoctors.select_both_dates'));
            }
            return;
        }

        // For custom, validate dates before fetching
        if (activeTab === 'custom') {
            if (!validateDates(startDate, endDate)) {
                return;
            }
        }

        const params = {
            from,
            to,
            page,
            limit: itemsPerPage,
        };
        if (filters.gender.length > 0) params.gender = filters.gender.join(',');
        if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');
        if (filters.selectedPatient.length > 0) {
            params.patientId = filters.selectedPatient.map((p) => p.patientId).join(',');
        }

        console.log('fetchDoctorsData - API Called - Page:', page, 'From:', from, 'To:', to, 'Limit:', itemsPerPage, 'Genders:', params.gender || 'All', 'Countries:', params.countryId || 'All', 'PatientIds:', params.patientId || 'All');
        dispatch(getDoctorsByDate(params));
    };

    // Fetch patient list when filter opens or search changes
    useEffect(() => {
        if (isFilterOpen) {
            dispatch(getAllPatientsList({ search: debouncedPatientSearch }));
        }
    }, [dispatch, debouncedPatientSearch, isFilterOpen]);

    // Fetch countries on mount
    useEffect(() => {
        dispatch(getCountries());
    }, [dispatch]);

    // Sync tempFilters with filters when popup opens
    useEffect(() => {
        if (isFilterOpen) {
            setTempFilters({ ...filters });
        }
    }, [isFilterOpen]);

    // Close filter on click outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (
                filterRef.current &&
                !filterRef.current.contains(event.target) &&
                filterButtonRef.current &&
                !filterButtonRef.current.contains(event.target)
            ) {
                // Reset tempFilters to current filters when closing without applying
                setTempFilters({ ...filters });
                setIsFilterOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [filters]);

    // Initial load - ONLY runs once on mount
    useEffect(() => {
        console.log('Component mounted - Initial API call');
        fetchDoctorsData(1);
        // Mark that initial render is complete
        setTimeout(() => {
            isInitialRender.current = false;
        }, 100);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Empty dependency array - runs ONLY once

    // Handle tab changes (skip initial render)
    useEffect(() => {
        if (!isInitialRender.current) {
            console.log('Tab changed to:', activeTab);
            // Clear dates when switching to custom tab
            if (activeTab === 'custom') {
                setStartDate('');
                setEndDate('');
                setDateErrors({ startDate: '', endDate: '' });
            } else {
                // Fetch data automatically for non-custom tabs
                setCurrentPage(1);
                fetchDoctorsData(1);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [activeTab]);

    // Handle custom date changes (skip initial render)
    useEffect(() => {
        if (!isInitialRender.current && activeTab === 'custom' && startDate && endDate) {
            console.log('Custom dates changed - From:', startDate, 'To:', endDate);
            setCurrentPage(1);
            fetchDoctorsData(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [startDate, endDate]);

    // Handle filter changes (skip initial render)
    useEffect(() => {
        if (!isInitialRender.current) {
            console.log('Filters changed - Gender:', filters.gender, 'CountryId:', filters.countryId);
            setCurrentPage(1);
            fetchDoctorsData(1);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [filters]);

    const handlePageChange = (page, newLimit) => {
        console.log('Pagination changed - Page:', page, 'Limit:', newLimit);
        setCurrentPage(page);

        // If limit changed, update the itemsPerPage state
        if (newLimit && newLimit !== itemsPerPage) {
            setItemsPerPage(newLimit);
        }

        // Fetch data with the new page and limit
        const { from, to } = getDateRange(activeTab);

        if (!from || !to) {
            if (activeTab === 'custom') {
                toast.warning(t('reportsDoctors.select_both_dates'));
            }
            return;
        }

        // For custom, validate dates before fetching
        if (activeTab === 'custom') {
            if (!validateDates(startDate, endDate)) {
                return;
            }
        }

        const limit = newLimit || itemsPerPage;
        const params = { from, to, page, limit };
        if (filters.gender.length > 0) params.gender = filters.gender.join(',');
        if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');
        if (filters.selectedPatient.length > 0) {
            params.patientId = filters.selectedPatient.map((p) => p.patientId).join(',');
        }

        console.log('API Called - Page:', page, 'From:', from, 'To:', to, 'Limit:', limit, 'Genders:', params.gender || 'All', 'Countries:', params.countryId || 'All', 'PatientIds:', params.patientId || 'All');
        dispatch(getDoctorsByDate(params));
    };

    const handleDownload = async () => {
        const { from, to } = getDateRange(activeTab);

        if (!from || !to) {
            toast.warning(t('reportsDoctors.select_valid_date_range'));
            return;
        }

        // For custom, validate dates before downloading
        if (activeTab === 'custom') {
            if (!validateDates(startDate, endDate)) {
                toast.warning(t('reportsDoctors.select_valid_date_range'));
                return;
            }
        }

        try {
            const params = { from, to };
            if (filters.gender.length > 0) params.gender = filters.gender.join(',');
            if (filters.countryId.length > 0) params.countryId = filters.countryId.join(',');
            if (filters.selectedPatient.length > 0) {
                params.patientId = filters.selectedPatient.map((p) => p.patientId).join(',');
            }

            const resultAction = await dispatch(downloadDoctorsExcel(params));

            if (downloadDoctorsExcel.fulfilled.match(resultAction)) {
                const blob = resultAction.payload;
                const url = window.URL.createObjectURL(blob);
                const link = document.createElement('a');
                link.href = url;
                link.download = `doctors-report-${from}-to-${to}.xlsx`;
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(url);
                toast.success(t('reportsDoctors.excel_download_success'));
            } else {
                toast.warning(t('reportsDoctors.excel_download_failed'));
            }
        } catch (error) {
            toast.warning(t('reportsDoctors.excel_download_error'));
        }
    };

    const indexOfFirstDoctor = (currentPage - 1) * itemsPerPage;

    // Show empty data when in custom tab without valid dates
    const shouldShowData = activeTab === 'custom' ? (startDate && endDate) : true;
    const displayDoctors = shouldShowData ? doctorsByDate : [];

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' });
    };

    // Handle view doctor details
    const handleViewDoctor = async (doctor) => {
        const doctorId = doctor.doctorId || doctor.id;

        if (!doctorId) {
            toast.warning(t('reportsDoctors.doctor_id_not_found'));
            return;
        }

        setIsModalOpen(true);
        try {
            await dispatch(getDoctorById(doctorId)).unwrap();
        } catch (error) {
            console.error('Error fetching doctor details:', error);
            toast.warning(t('reportsDoctors.failed_load_doctor'));
            setIsModalOpen(false);
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            className="bg-white w-full rounded-2xl p-4 sm:p-6 shadow-md"
            data-testid="reports-doctors-container"
        >
            {/* Header - Exactly like Doctor page */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6 gap-4" data-testid="reports-doctors-header">
                <h3 className="text-2xl sm:text-3xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent flex items-center gap-3" data-testid="reports-doctors-title">
                    {t('reportsDoctors.title')}
                </h3>

                <motion.button
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={handleDownload}
                    disabled={downloadLoading || displayDoctors.length === 0}
                    className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-sm hover:shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
                    data-testid="reports-doctors-download-button"
                >
                    <Download className="w-4 h-4" data-testid="reports-doctors-download-icon" />
                    {downloadLoading ? t('reportsDoctors.downloading') : t('reportsDoctors.download_excel')}
                </motion.button>
            </div>

            {/* Tabs, Patient Selection & Filter - In Same Row */}
            <div className="flex flex-col gap-4 mb-5" data-testid="reports-doctors-tabs-filter-section">
                <div className="flex flex-col sm:flex-row items-start sm:items-center justify-start gap-2">
                    {/* Tabs */}
                    <div className="grid grid-cols-2 sm:flex sm:flex-row gap-2 bg-gradient-to-r from-gray-50 to-blue-50/30 w-fit rounded-2xl p-1 shadow border border-gray-200 relative" data-testid="reports-doctors-tabs-container">
                        {['today', 'week', 'month', 'custom'].map((tab) => (
                            <button
                                key={tab}
                                onClick={() => setActiveTab(tab)}
                                className={`relative px-3 py-2 text-xs sm:text-sm font-medium rounded-xl transition-all duration-300 cursor-pointer z-10 ${activeTab === tab
                                    ? 'text-white'
                                    : 'text-gray-600 hover:text-[#4ABDBD]'
                                    }`}
                                data-testid={`reports-doctors-tab-${tab}`}
                            >
                                {activeTab === tab && (
                                    <motion.div
                                        layoutId="activeDoctorReportTab"
                                        className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-xl shadow-lg !m-0"
                                        transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                    />
                                )}
                                <span className="relative z-20" data-testid={`reports-doctors-tab-text-${tab}`}>
                                    {t(`reportsDoctors.${tab}`)}
                                </span>
                            </button>
                        ))}
                    </div>

                    {/* Filter Button */}
                    <div className="relative w-full sm:w-auto" data-testid="reports-doctors-filter-container">
                        <button
                            ref={filterButtonRef}
                            onClick={() => setIsFilterOpen(!isFilterOpen)}
                            className="group flex items-center justify-center gap-2 h-full px-3 py-2.5 text-white bg-gradient-to-br from-[#4ABDBD] to-[#3A9B9B] rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer"
                            aria-label={t('reportsDoctors.filter')}
                            aria-expanded={isFilterOpen}
                            data-testid="reports-doctors-filter-button"
                        >
                            <SlidersHorizontal className="w-5 h-5" data-testid="reports-doctors-filter-icon" />
                            {/* {t('reportsDoctors.filter')} */}
                        </button>

                        {isFilterOpen && (
                            <FilterPopover ref={filterRef}>
                                <FilterPanel
                                    onClear={() => {
                                        setTempFilters({ gender: [], countryId: [], selectedPatient: [] });
                                        setFilters({ gender: [], countryId: [], selectedPatient: [] });
                                        setPatientSearch('');
                                        setCurrentPage(1);
                                        setIsFilterOpen(false);
                                    }}
                                    onApply={() => {
                                        setFilters(tempFilters);
                                        setCurrentPage(1);
                                        setIsFilterOpen(false);
                                    }}
                                    clearLabel={t('reportsDoctors.filter_clear')}
                                    applyLabel={t('reportsDoctors.filter_apply')}
                                >
                                    <FilterSection title={t('reportsDoctors.filter_gender')} isActive={tempFilters.gender.length > 0}>
                                        <FilterOptionList
                                            multiple
                                            value={tempFilters.gender}
                                            onChange={(val) => setTempFilters({ ...tempFilters, gender: val })}
                                            options={[
                                                { value: 'male', label: t('reportsDoctors.filter_male') },
                                                { value: 'female', label: t('reportsDoctors.filter_female') },
                                                { value: 'Not_Communicated', label: t('reportsDoctors.filter_Not_Communicated') },
                                                { value: 'other', label: t('reportsDoctors.filter_others') },
                                            ]}
                                        />
                                    </FilterSection>

                                    <FilterSection title={t('reportsDoctors.filter_country', 'Country')} isActive={tempFilters.countryId.length > 0}>
                                        <FilterCountryList
                                            multiple
                                            value={tempFilters.countryId}
                                            onChange={(val) => setTempFilters({ ...tempFilters, countryId: val })}
                                            countries={countries}
                                            searchPlaceholder={t('reportsDoctors.search_placeholder', 'Search...')}
                                        />
                                    </FilterSection>

                                    <FilterSection title={t('reportsDoctors.filter_patient', 'Patient')} isActive={tempFilters.selectedPatient.length > 0}>
                                        <FilterSearchableList
                                            multiple
                                            items={allPatientsList || []}
                                            value={tempFilters.selectedPatient}
                                            onChange={(val) => setTempFilters({ ...tempFilters, selectedPatient: val })}
                                            labelKey="name"
                                            sublabelKey="surname"
                                            valueKey="patientId"
                                            searchPlaceholder={t('reportsDoctors.search_patient', 'Search patient...')}
                                            noResultsText={t('reportsDoctors.no_patients', 'No patients found')}
                                            allLabel={t('reportsDoctors.all_patients', 'All Patients')}
                                            loading={patientsLoading}
                                            onSearch={(val) => setPatientSearch(val)}
                                        />
                                    </FilterSection>
                                </FilterPanel>
                            </FilterPopover>
                        )}
                    </div>
                </div>

                {activeTab === 'custom' && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="flex flex-col sm:flex-row gap-2"
                        data-testid="reports-doctors-custom-date-section"
                    >
                        <div className="w-full sm:flex-1" data-testid="reports-doctors-from-date-field-container">
                            <CustomDatePicker
                                value={startDate}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setStartDate(value);
                                    // Validate dates when start date changes
                                    if (value || endDate) {
                                        validateDates(value, endDate);
                                    } else {
                                        setDateErrors({ startDate: '', endDate: '' });
                                    }
                                }}
                                placeholder={t('reportsDoctors.from_date')}
                            />
                            {dateErrors.startDate && (
                                <p className="text-red-600 text-xs mt-1" data-testid="reports-doctors-from-date-error">{dateErrors.startDate}</p>
                            )}
                        </div>
                        <div className="w-full sm:flex-1" data-testid="reports-doctors-to-date-field-container">
                            <CustomDatePicker
                                value={endDate}
                                onChange={(e) => {
                                    const value = e.target.value;
                                    setEndDate(value);
                                    // Validate dates when end date changes
                                    if (value || startDate) {
                                        validateDates(startDate, value);
                                    } else {
                                        setDateErrors({ startDate: '', endDate: '' });
                                    }
                                }}
                                minDate={startDate}
                                maxDate={new Date().toISOString().split('T')[0]}
                                placeholder={t('reportsDoctors.to_date')}
                            />
                            {dateErrors.endDate && (
                                <p className="text-red-600 text-xs mt-1" data-testid="reports-doctors-to-date-error">{dateErrors.endDate}</p>
                            )}
                        </div>
                        <motion.button
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                            onClick={() => {
                                setStartDate('');
                                setEndDate('');
                                setDateErrors({ startDate: '', endDate: '' });
                                setCurrentPage(1);
                            }}
                            className="flex items-center justify-center gap-1.5 text-xs font-medium bg-gray-100 text-gray-700 rounded-lg px-4 py-2 hover:bg-[#4ABDBD]/10 hover:text-[#4ABDBD] transition-all"
                            data-testid="reports-doctors-reset-button"
                        >
                            <RotateCcw className="w-4 h-4" data-testid="reports-doctors-reset-icon" />
                            {t('reportsDoctors.reset')}
                        </motion.button>
                    </motion.div>
                )}
            </div>

            {/* Applied Filters Display */}
            {(filters.gender.length > 0 || filters.countryId.length > 0 || filters.selectedPatient.length > 0) && (
                <div className="mb-6 bg-gradient-to-r from-blue-50/80 to-cyan-50/80 rounded-xl p-4 border border-gray-200 shadow-sm" data-testid="reports-doctors-applied-filters-container">
                    <div className="flex flex-wrap items-center gap-2">
                        <span className="text-sm font-semibold text-gray-700 mr-2" data-testid="reports-doctors-applied-filters-label">
                            {t('reportsDoctors.applied_filters')}:
                        </span>

                        {filters.gender.map((g) => (
                            <div key={`gender-${g}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-doctors-applied-gender-chip-${g}`}>
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('reportsDoctors.filter_gender')}:</span> {t(`reportsDoctors.filter_${g}`)}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, gender: filters.gender.filter((x) => x !== g) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                    data-testid={`reports-doctors-applied-gender-remove-button-${g}`}
                                >
                                    <X className="h-3.5 w-3.5" data-testid={`reports-doctors-applied-gender-remove-icon-${g}`} />
                                </button>
                            </div>
                        ))}

                        {filters.countryId.map((cid) => (
                            <div key={`country-${cid}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-doctors-applied-country-chip-${cid}`}>
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('reportsDoctors.filter_country', 'Country')}:</span> <MasterLabel type="masterCountry" id={cid} field="countryName" fallback={countries?.find(c => c.countryId === cid)?.countryName || cid} />
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, countryId: filters.countryId.filter((x) => x !== cid) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                    data-testid={`reports-doctors-applied-country-remove-button-${cid}`}
                                >
                                    <X className="h-3.5 w-3.5" data-testid={`reports-doctors-applied-country-remove-icon-${cid}`} />
                                </button>
                            </div>
                        ))}

                        {filters.selectedPatient.map((p) => (
                            <div key={`patient-${p.patientId}`} className="inline-flex items-center gap-2 bg-white px-3 py-1.5 rounded-lg border border-gray-300 shadow-sm" data-testid={`reports-doctors-applied-patient-chip-${p.patientId}`}>
                                <span className="text-xs text-gray-600">
                                    <span className="font-semibold">{t('reportsDoctors.filter_patient', 'Patient')}:</span> {`${p.name || ''} ${p.surname || ''}`.trim()}
                                </span>
                                <button
                                    onClick={() => setFilters({ ...filters, selectedPatient: filters.selectedPatient.filter((x) => x.patientId !== p.patientId) })}
                                    className="text-gray-400 hover:text-red-500 transition-colors duration-200 cursor-pointer"
                                    data-testid={`reports-doctors-applied-patient-remove-button-${p.patientId}`}
                                >
                                    <X className="h-3.5 w-3.5" data-testid={`reports-doctors-applied-patient-remove-icon-${p.patientId}`} />
                                </button>
                            </div>
                        ))}

                        <button
                            onClick={() => {
                                setFilters({ gender: [], countryId: [], selectedPatient: [] });
                                setTempFilters({ gender: [], countryId: [], selectedPatient: [] });
                                setCurrentPage(1);
                            }}
                            className="ml-auto inline-flex items-center gap-1.5 bg-gradient-to-r from-red-500 to-red-600 text-white px-3 py-1.5 rounded-lg text-xs font-semibold hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md cursor-pointer"
                            data-testid="reports-doctors-clear-all-button"
                        >
                            <X className="h-3.5 w-3.5" data-testid="reports-doctors-clear-all-icon" />
                            {t('reportsDoctors.clear_all')}
                        </button>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loading && (
                <div className="flex flex-col items-center justify-center py-4 pt-12" data-testid="reports-doctors-loading-container">
                    <CommonLoader />
                    <span className="text-xl text-[#4ABDBD]" data-testid="reports-doctors-loading-text">{t('reportsDoctors.loading')}</span>
                </div>
            )}

            {/* Table - EXACT style from Doctor page */}
            {!loading && (
                <div className="hidden sm:block overflow-x-auto rounded-md border border-gray-200" data-testid="reports-doctors-table-container">
                    <table className="w-full border-collapse rounded-md overflow-hidden" data-testid="reports-doctors-table">
                        <thead data-testid="reports-doctors-table-head">
                            <tr className="bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white" data-testid="reports-doctors-table-header-row">
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctors-header-serial">{t('reportsDoctors.serial_no')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctors-header-name">{t('reportsDoctors.name')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctors-header-email">{t('reportsDoctors.email')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctors-header-phone">{t('reportsDoctors.phone')}</th>
                                <th className="px-4 py-3 text-left text-xs font-bold uppercase tracking-wider" data-testid="reports-doctors-header-license">{t('reportsDoctors.license_no')}</th>
                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctors-header-gender">{t('reportsDoctors.gender')}</th>
                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctors-header-created-at">{t('reportsDoctors.created_at')}</th>
                                <th className="px-4 py-3 text-center text-xs font-bold uppercase tracking-wider" data-testid="reports-doctors-header-action">{t('reportsDoctors.action')}</th>
                            </tr>
                        </thead>
                        <tbody data-testid="reports-doctors-table-body">
                            {displayDoctors.length === 0 ? (
                                <tr data-testid="reports-doctors-empty-row">
                                    <td colSpan="8" className="p-8 text-center text-gray-500" data-testid="reports-doctors-no-data-text">
                                        {t('reportsDoctors.no_data')}
                                    </td>
                                </tr>
                            ) : (
                                displayDoctors.map((doc, index) => {
                                    const serialNo = indexOfFirstDoctor + index + 1;
                                    const fullName = `${doc.name || ''} ${doc.surname || ''}`.trim();
                                    const formattedCreatedAt = formatDate(doc.createdAt);
                                    return (
                                        <tr
                                            key={doc._id || doc.id || doc.doctorId || `doctor-${index}`}
                                            className="border-b border-gray-100/50 hover:bg-gradient-to-r hover:from-blue-50/80 hover:to-cyan-50/80 transition-all duration-300 group"
                                            data-testid={`reports-doctors-row-${index}`}
                                        >
                                            <td className="px-4 py-5 text-xs text-gray-600 group-hover:text-gray-700" data-testid={`reports-doctors-serial-${index}`}>{serialNo}</td>
                                            <td className="px-4 py-5 text-xs font-medium text-gray-800 group-hover:text-gray-900" data-testid={`reports-doctors-name-${index}`}>{fullName || '-'}</td>
                                            <td className="px-4 py-5 text-xs text-gray-600" data-testid={`reports-doctors-email-${index}`}>{doc.email || '-'}</td>
                                            <td className="px-4 py-5 text-xs text-gray-600" data-testid={`reports-doctors-phone-${index}`}>{doc.phone || '-'}</td>
                                            <td className="px-4 py-5 text-xs text-gray-600" data-testid={`reports-doctors-license-${index}`}>{doc.licenseNumber || '-'}</td>
                                            <td className="px-4 py-5 text-center text-xs text-gray-600 capitalize" data-testid={`reports-doctors-gender-${index}`}>{doc.gender ? t(`common.gender.${doc.gender.toLowerCase()}`) : '-'}</td>
                                            <td className="px-4 py-5 text-center text-xs text-gray-600" data-testid={`reports-doctors-created-at-${index}`}>{formattedCreatedAt}</td>
                                            <td className="px-4 py-5 text-center" data-testid={`reports-doctors-action-cell-${index}`}>
                                                <button
                                                    onClick={() => handleViewDoctor(doc)}
                                                    className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden inline-flex items-center justify-center"
                                                    data-testid={`reports-doctors-view-button-${index}`}
                                                >
                                                    <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                                    <Eye className="h-4 w-4 relative z-10" data-testid={`reports-doctors-view-icon-${index}`} />
                                                </button>
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {/* Mobile View - Simple cards (kept minimal as per your original) */}
            {!loading && (
                <div className="block sm:hidden space-y-4" data-testid="reports-doctors-mobile-container">
                    {displayDoctors.length === 0 ? (
                        <div className="p-8 text-center text-gray-500" data-testid="reports-doctors-mobile-no-data">
                            {t('reportsDoctors.no_data')}
                        </div>
                    ) : (
                        displayDoctors.map((doc, index) => {
                            const serialNo = indexOfFirstDoctor + index + 1;
                            const fullName = `${doc.name || ''} ${doc.surname || ''}`.trim();
                            const formattedCreatedAt = formatDate(doc.createdAt);
                            return (
                                <div key={doc._id || doc.id || doc.doctorId || `doctor-mobile-${index}`} className="p-4 bg-gray-50 rounded-xl border border-gray-200" data-testid={`reports-doctors-mobile-card-${index}`}>
                                    <div className="flex justify-between items-start mb-2">
                                        <span className="text-xs font-bold text-[#4ABDBD]" data-testid={`reports-doctors-mobile-serial-${index}`}>{t('reportsDoctors.serial_no')}: {serialNo}</span>
                                        <button
                                            onClick={() => handleViewDoctor(doc)}
                                            className="group/btn text-[#4ABDBD] hover:text-white p-2 rounded-xl bg-gradient-to-r from-blue-50 to-cyan-50 hover:from-[#4ABDBD] hover:to-[#3AA8A8] shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                                            data-testid={`reports-doctors-mobile-view-button-${index}`}
                                        >
                                            <div className="absolute inset-0 bg-gradient-to-r from-white/20 to-transparent opacity-0 group-hover/btn:opacity-100 transition-opacity duration-300"></div>
                                            <Eye className="h-4 w-4 relative z-10" data-testid={`reports-doctors-mobile-view-icon-${index}`} />
                                        </button>
                                    </div>
                                    <div className="text-sm font-medium mb-2" data-testid={`reports-doctors-mobile-name-${index}`}>{fullName || '-'}</div>
                                    <div className="text-xs text-gray-600" data-testid={`reports-doctors-mobile-email-${index}`}>{t('reportsDoctors.email_label')}: {doc.email || '-'}</div>
                                    <div className="text-xs text-gray-600" data-testid={`reports-doctors-mobile-phone-${index}`}>{t('reportsDoctors.phone_label')}: {doc.phone || '-'}</div>
                                    <div className="text-xs text-gray-600" data-testid={`reports-doctors-mobile-license-${index}`}>{t('reportsDoctors.license_label')}: {doc.licenseNumber || '-'}</div>
                                    <div className="text-xs text-gray-600 capitalize" data-testid={`reports-doctors-mobile-gender-${index}`}>{t('reportsDoctors.gender_label')}: {doc.gender ? t(`common.gender.${doc.gender.toLowerCase()}`) : '-'}</div>
                                    <div className="text-xs text-gray-600" data-testid={`reports-doctors-mobile-created-at-${index}`}>{t('reportsDoctors.created_at_label')}: {formattedCreatedAt}</div>
                                </div>
                            );
                        })
                    )}
                </div>
            )}

            {/* Pagination - Same as Doctor page */}
            {!loading && (shouldShowData ? totalItems : 0) > 0 && (
                <div className="" data-testid="reports-doctors-pagination-container">
                    <Pagination
                        totalItems={shouldShowData ? totalItems : 0}
                        currentPage={currentPage}
                        itemsPerPage={itemsPerPage}
                        onPageChange={handlePageChange}
                        custombutton={displayDoctors.length === 0 ? 'disable' : ''}
                    />
                </div>
            )}

            {/* Doctor Profile Modal */}
            <DoctorProfileModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false);
                }}
                doctor={selectedDoctor}
                loading={doctorLoading}
            />
        </motion.div>
    );
};

export default ReportsDoctors;